// API client for AI Cost Attribution (cost of AI by user / by endpoint).

export interface UserCostRow {
  user_key: string;
  user_label: string;
  source: 'corporate' | 'local' | 'endpoint' | 'agent';
  sanctioned_tokens: number;
  shadow_tokens: number;
  tokens: number;
  sanctioned_usd: number;
  shadow_usd: number;
  est_total_usd: number;
  requests: number;
  all_shadow: boolean;
}

export interface EndpointUserSlice {
  user_label: string;
  source: 'corporate' | 'local' | 'endpoint' | 'agent';
  tokens: number;
  est_usd: number;
}

export interface EndpointCostRow {
  device_id: string;
  hostname: string | null;
  os: string | null;
  shared: boolean;
  users: string[];
  per_user: EndpointUserSlice[];
  shadow_tokens: number;
  est_usd: number;
  top_apps: string[];
  requests: number;
}

export interface CostSummary {
  period: string;
  sanctioned_tokens: number;
  shadow_tokens: number;
  sanctioned_usd: number;
  shadow_usd: number;
  est_total_usd: number;
  user_count: number;
  endpoint_count: number;
  top_user: { label: string; usd: number } | null;
  top_endpoint: { label: string; usd: number } | null;
}

export interface RateRow {
  model_pattern: string;
  in_rate_mtok: number;
  out_rate_mtok: number;
  updated_by?: string | null;
  updated_at?: string | null;
}

async function parseError(res: Response): Promise<never> {
  const err = await res.json().catch(() => ({}));
  throw new Error(err.message || err.error || `Request failed (${res.status})`);
}

export async function fetchSummary(apiUrl: string, period?: string): Promise<CostSummary> {
  const qs = period ? `?period=${encodeURIComponent(period)}` : '';
  const res = await fetch(`${apiUrl}/admin/ai-cost/summary${qs}`);
  if (!res.ok) return parseError(res);
  return res.json();
}

export async function fetchByUser(apiUrl: string, period?: string): Promise<UserCostRow[]> {
  const qs = period ? `?period=${encodeURIComponent(period)}` : '';
  const res = await fetch(`${apiUrl}/admin/ai-cost/by-user${qs}`);
  if (!res.ok) return parseError(res);
  return (await res.json()).users || [];
}

export async function fetchByEndpoint(apiUrl: string): Promise<EndpointCostRow[]> {
  const res = await fetch(`${apiUrl}/admin/ai-cost/by-endpoint`);
  if (!res.ok) return parseError(res);
  return (await res.json()).endpoints || [];
}

export async function fetchRates(apiUrl: string): Promise<RateRow[]> {
  const res = await fetch(`${apiUrl}/admin/ai-cost/rates`);
  if (!res.ok) return parseError(res);
  return (await res.json()).rates || [];
}

export async function updateRate(
  apiUrl: string,
  pattern: string,
  inMtok: number,
  outMtok: number
): Promise<void> {
  const res = await fetch(`${apiUrl}/admin/ai-cost/rates/${encodeURIComponent(pattern)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ in_rate_mtok: inMtok, out_rate_mtok: outMtok }),
  });
  if (!res.ok) return parseError(res);
}

/** Build a download URL for the token CSV export. */
export function exportUrl(apiUrl: string, group: 'user' | 'endpoint' | 'model', period?: string): string {
  const p = period ? `&period=${encodeURIComponent(period)}` : '';
  return `${apiUrl}/admin/ai-cost/export?group=${group}${p}`;
}
